/**
 * Live hotel search panel — LiteAPI results + stay-area controls.
 * Used by Trip Lodging (Add Hotel) and City Planning Hotels.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Slider,
  Checkbox,
  Spin,
  message,
  Segmented,
} from 'antd';
import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Trip, TripHotelStay } from '../../types/client';
import type { AggregatedPlanData } from '../../utils/aggregatedPlanData';
import { tripLodgingEn } from '../../content/tripLodging.en';
import {
  searchHotels,
  fetchHotelFilters,
  createHotelReservation,
  getHotelReservation,
  offerDisplayPrice,
  type HotelSearchOffer,
  type LiteAPIFilterCatalog,
  type LiteAPISearchFilters,
} from '../../services/hotelsApi';
import {
  initialStayAreaSelection,
  resolveHotelSearchCenter,
  type HotelStayAreaSelection,
} from '../../utils/hotelSearchCenter';
import { newHotelStayId } from '../../utils/hotelStayHelpers';
import { loadLocalUserPreferences } from '../../services/userPreferencesSyncService';
import { isOnline } from '../../services/viatorProductsService';
import { visibleHotels } from '../../utils/hotelCatalogSearch';
import styles from '../../pages/TripLodging/TripLodging.module.css';

const PAGE_SIZE = 12;

type OfferSort = 'distance' | 'price' | 'rating' | 'stars';

function sortOffers(offers: HotelSearchOffer[], sort: OfferSort): HotelSearchOffer[] {
  const list = [...offers];
  list.sort((a, b) => {
    if (sort === 'price') {
      return (offerDisplayPrice(a) ?? Number.POSITIVE_INFINITY) -
        (offerDisplayPrice(b) ?? Number.POSITIVE_INFINITY);
    }
    if (sort === 'rating') {
      return (b.rating ?? 0) - (a.rating ?? 0);
    }
    if (sort === 'stars') {
      return (b.stars ?? 0) - (a.stars ?? 0);
    }
    return 0;
  });
  return list;
}

function offerToStay(
  offer: HotelSearchOffer,
  checkIn: string,
  checkOut: string,
  confirmationNumber?: string
): TripHotelStay {
  const confirmations = confirmationNumber?.trim()
    ? [{ id: crypto.randomUUID(), confirmationNumber: confirmationNumber.trim() }]
    : [];
  return {
    id: newHotelStayId(),
    hotelName: offer.name,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    address: offer.formattedAddress ?? null,
    phone: null,
    notes: offer.roomName ? `Room: ${offer.roomName}` : null,
    url: null,
    provider: offer.provider ?? 'liteapi',
    hotelId: offer.hotelId,
    offerId: offer.offerId,
    deepLink: null,
    photoUrl: offer.photoUrl ?? offer.photoUrls?.[0] ?? null,
    rating: offer.rating ?? null,
    latitude: offer.latitude ?? null,
    longitude: offer.longitude ?? null,
    confirmations,
    bookingKey: offer.bookingKey ?? null,
  };
}

const HotelLiveSearchPanel: React.FC<{
  trip: Trip;
  cityVisitId: string;
  cityName: string;
  plan: AggregatedPlanData | null;
  checkIn: string;
  checkOut: string;
  canEdit: boolean;
  currencyCode?: string | null;
  onSaved: (stay: TripHotelStay) => void;
  onEnterManually?: () => void;
}> = ({
  trip,
  cityVisitId,
  cityName,
  plan,
  checkIn: defaultCheckIn,
  checkOut: defaultCheckOut,
  canEdit,
  currencyCode,
  onSaved,
  onEnterManually,
}) => {
  const [selection, setSelection] = useState<HotelStayAreaSelection>(() =>
    initialStayAreaSelection(plan)
  );
  const [offers, setOffers] = useState<HotelSearchOffer[]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<OfferSort>('distance');
  const [catalog, setCatalog] = useState<LiteAPIFilterCatalog | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<LiteAPISearchFilters>({});
  const [draftFilters, setDraftFilters] = useState<LiteAPISearchFilters>({});
  const [saveOffer, setSaveOffer] = useState<HotelSearchOffer | null>(null);
  const [bookOffer, setBookOffer] = useState<HotelSearchOffer | null>(null);
  const [nameSearchText, setNameSearchText] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState<string | null>(null);
  const [nameSearchError, setNameSearchError] = useState<string | null>(null);
  const [online, setOnline] = useState(() => isOnline());
  const activeSearchTermRef = useRef<string | null>(null);
  const searchRequestId = useRef(0);

  const center = useMemo(
    () =>
      resolveHotelSearchCenter({
        selection,
        plan,
        trip,
        cityVisitId,
        cityName,
      }),
    [selection, plan, trip, cityVisitId, cityName]
  );

  const countryCode = useMemo(() => {
    const prefs = loadLocalUserPreferences();
    const raw = prefs.appUserSettings?.userNationality?.trim().toUpperCase();
    if (raw && raw.length === 2) return raw;
    return 'US';
  }, []);

  const displayCurrency =
    currencyCode?.trim() || trip.currency?.trim() || 'USD';

  const runSearch = useCallback(
    async (reset: boolean, nameTerm?: string | null) => {
      const requestId = ++searchRequestId.current;
      if (!center) {
        setError('No search location available for this city.');
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      const resolvedTerm = reset
        ? nameTerm?.trim() || null
        : activeSearchTermRef.current;
      if (reset) {
        activeSearchTermRef.current = resolvedTerm;
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const nextOffset = reset ? 0 : offset;
        const res = await searchHotels({
          destination:
            center.kind === 'cityVisit'
              ? cityName
              : `${cityName} — ${center.name}`,
          searchTerm: resolvedTerm || undefined,
          checkIn: defaultCheckIn,
          checkOut: defaultCheckOut,
          latitude: center.latitude,
          longitude: center.longitude,
          radiusKm: center.radiusKm,
          cityVisitId,
          limit: PAGE_SIZE,
          offset: nextOffset,
          currency: displayCurrency,
          countryCode,
          filters: Object.keys(appliedFilters).length ? appliedFilters : undefined,
        });
        if (requestId !== searchRequestId.current) return;
        setOffers((prev) => (reset ? res.offers : [...prev, ...res.offers]));
        setOffset(nextOffset + res.offers.length);
      } catch (err) {
        if (requestId !== searchRequestId.current) return;
        setError(err instanceof Error ? err.message : tripLodgingEn.searchError);
      } finally {
        if (requestId === searchRequestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      center,
      cityName,
      cityVisitId,
      defaultCheckIn,
      defaultCheckOut,
      displayCurrency,
      countryCode,
      appliedFilters,
      offset,
    ]
  );

  useEffect(() => {
    void fetchHotelFilters()
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, []);

  useEffect(() => {
    setSelection(initialStayAreaSelection(plan));
  }, [plan]);

  useEffect(() => {
    const syncOnline = (): void => {
      const next = isOnline();
      setOnline(next);
      if (next) setNameSearchError(null);
    };
    window.addEventListener('online', syncOnline);
    window.addEventListener('offline', syncOnline);
    return () => {
      window.removeEventListener('online', syncOnline);
      window.removeEventListener('offline', syncOnline);
    };
  }, []);

  useEffect(() => {
    setNameSearchText('');
    setSubmittedSearchTerm(null);
    setNameSearchError(null);
    activeSearchTermRef.current = null;
    setOffers([]);
    setOffset(0);
    void runSearch(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: re-search when center/filters/dates change
  }, [center?.latitude, center?.longitude, center?.radiusKm, appliedFilters, defaultCheckIn, defaultCheckOut]);

  const ranked = useMemo(() => sortOffers(offers, sort), [offers, sort]);
  const displayed = useMemo(
    () =>
      visibleHotels({
        loaded: ranked,
        query: nameSearchText,
        submittedQuery: submittedSearchTerm,
      }),
    [ranked, nameSearchText, submittedSearchTerm]
  );
  const trimmedNameQuery = nameSearchText.trim();
  const submittedName = submittedSearchTerm?.trim() ?? '';
  const showingServerNameResults =
    submittedName.length > 0 &&
    submittedName.toLowerCase() === trimmedNameQuery.toLowerCase();
  const nameSearchLoading = loading && submittedSearchTerm != null;
  const emptyCopy = showingServerNameResults
    ? tripLodgingEn.hotelNameSearchEmpty
    : trimmedNameQuery && offers.length > 0
      ? tripLodgingEn.hotelNameSearchNoLocal
      : tripLodgingEn.searchEmpty;

  const handleNameSearchChange = (value: string): void => {
    setNameSearchText(value);
    setNameSearchError(null);
    const trimmed = value.trim();
    setSubmittedSearchTerm((prev) => {
      if (!prev?.trim()) return prev;
      if (prev.trim().toLowerCase() === trimmed.toLowerCase()) return prev;
      return null;
    });
  };

  const handleSubmitNameSearch = async (): Promise<void> => {
    const term = nameSearchText.trim();
    if (!term || nameSearchLoading) return;
    if (!isOnline()) {
      setOnline(false);
      setNameSearchError(tripLodgingEn.hotelNameSearchOffline);
      return;
    }
    setNameSearchError(null);
    setSubmittedSearchTerm(term);
    setOffers([]);
    setOffset(0);
    await runSearch(true, term);
  };

  const handleClearNameSearch = async (): Promise<void> => {
    setNameSearchText('');
    setSubmittedSearchTerm(null);
    setNameSearchError(null);
    setOffers([]);
    setOffset(0);
    await runSearch(true, null);
  };
  const canShowMore = offers.length > 0 && offers.length % PAGE_SIZE === 0;

  const stayArea = plan?.recommendedStayArea;
  const mapSrc =
    center != null
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${
          center.longitude - 0.05
        }%2C${center.latitude - 0.03}%2C${center.longitude + 0.05}%2C${
          center.latitude + 0.03
        }&layer=mapnik&marker=${center.latitude}%2C${center.longitude}`
      : null;

  return (
    <div>
      {stayArea ? (
        <div className={styles.stayAreaCard}>
          <p className={styles.stayAreaTitle}>
            {tripLodgingEn.stayArea}: {stayArea.name}
          </p>
          {stayArea.rationale ? (
            <p className={styles.stayAreaMeta}>
              {tripLodgingEn.stayAreaRationale}: {stayArea.rationale}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={styles.searchToolbar}>
        <Segmented
          value={selection}
          onChange={(v) => setSelection(v as HotelStayAreaSelection)}
          options={[
            {
              value: 'recommended',
              label: tripLodgingEn.useRecommended,
              disabled: !stayArea,
            },
            { value: 'cityCenter', label: tripLodgingEn.cityCenter },
          ]}
        />
        <Select
          value={sort}
          onChange={setSort}
          style={{ width: 140 }}
          options={[
            { value: 'distance', label: tripLodgingEn.sortDistance },
            { value: 'price', label: tripLodgingEn.sortPrice },
            { value: 'rating', label: tripLodgingEn.sortRating },
            { value: 'stars', label: tripLodgingEn.sortStars },
          ]}
          aria-label={tripLodgingEn.sort}
        />
        <Button
          onClick={() => {
            setDraftFilters(appliedFilters);
            setFiltersOpen(true);
          }}
        >
          {tripLodgingEn.filters}
        </Button>
        {onEnterManually ? (
          <Button type="link" onClick={onEnterManually}>
            {tripLodgingEn.enterManually}
          </Button>
        ) : null}
      </div>

      <div className={styles.hotelNameSearch}>
        <Input
          prefix={<SearchOutlined aria-hidden />}
          placeholder={tripLodgingEn.hotelNameSearchPlaceholder}
          aria-label={tripLodgingEn.hotelNameSearchLabel}
          value={nameSearchText}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => handleNameSearchChange(e.target.value)}
          onPressEnter={() => void handleSubmitNameSearch()}
          suffix={
            nameSearchLoading ? (
              <Spin size="small" />
            ) : nameSearchText ? (
              <button
                type="button"
                className={styles.hotelNameClear}
                aria-label={tripLodgingEn.hotelNameSearchClear}
                onClick={() => void handleClearNameSearch()}
              >
                <CloseCircleFilled />
              </button>
            ) : (
              <span />
            )
          }
        />
        <Button
          type="primary"
          loading={nameSearchLoading}
          disabled={!trimmedNameQuery || !online || !center}
          onClick={() => void handleSubmitNameSearch()}
        >
          {tripLodgingEn.hotelNameSearchAction}
        </Button>
      </div>
      {!online ? (
        <p className={styles.hotelNameHint}>{tripLodgingEn.hotelNameSearchOffline}</p>
      ) : nameSearchError ? (
        <Alert
          type="warning"
          showIcon
          message={nameSearchError}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {mapSrc ? (
        <div className={styles.mapBlock}>
          <iframe title="Hotel search map" src={mapSrc} loading="lazy" />
        </div>
      ) : null}

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={() => void runSearch(true)}>
              {tripLodgingEn.retry}
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {loading ? (
        <div className={styles.loading}>
          <Spin tip={tripLodgingEn.searching} />
        </div>
      ) : null}

      {!loading && displayed.length === 0 && !error ? (
        <p className={styles.emptyDesc}>{emptyCopy}</p>
      ) : null}

      {displayed.map((offer) => {
        const price = offerDisplayPrice(offer);
        const photo = offer.photoUrl ?? offer.photoUrls?.[0];
        return (
          <article key={offer.offerId} className={styles.offerCard}>
            {photo ? (
              <img src={photo} alt="" className={styles.offerPhoto} />
            ) : (
              <div className={styles.offerPhoto} aria-hidden />
            )}
            <div className={styles.offerBody}>
              <p className={styles.offerName}>{offer.name}</p>
              <p className={styles.offerMeta}>
                {[
                  offer.roomName,
                  offer.stars != null ? `${offer.stars}★` : null,
                  offer.rating != null ? `★ ${offer.rating}` : null,
                  price != null
                    ? `${offer.currency || displayCurrency} ${Math.round(price)}`
                    : null,
                  offer.formattedAddress,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {canEdit ? (
                <div className={styles.offerActions}>
                  <Button size="small" type="primary" onClick={() => setSaveOffer(offer)}>
                    {tripLodgingEn.addToTrip}
                  </Button>
                  <Button size="small" onClick={() => setBookOffer(offer)}>
                    {tripLodgingEn.bookNow}
                  </Button>
                </div>
              ) : null}
            </div>
          </article>
        );
      })}

      {canShowMore && !loading ? (
        <Button
          block
          loading={loadingMore}
          onClick={() => void runSearch(false)}
          style={{ marginTop: 8 }}
        >
          {tripLodgingEn.showMore}
        </Button>
      ) : null}

      <Modal
        open={filtersOpen}
        title={tripLodgingEn.filters}
        onCancel={() => setFiltersOpen(false)}
        onOk={() => {
          setAppliedFilters(draftFilters);
          setFiltersOpen(false);
        }}
        okText={tripLodgingEn.applyFilters}
      >
        {catalog ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div>Stars</div>
              <Slider
                range
                min={catalog.stars.min}
                max={catalog.stars.max}
                value={[
                  draftFilters.starsMin ?? catalog.stars.min,
                  draftFilters.starsMax ?? catalog.stars.max,
                ]}
                onChange={(v) =>
                  setDraftFilters((f) => ({
                    ...f,
                    starsMin: v[0],
                    starsMax: v[1],
                  }))
                }
              />
            </div>
            {catalog.refundableSupported ? (
              <Checkbox
                checked={Boolean(draftFilters.refundableOnly)}
                onChange={(e) =>
                  setDraftFilters((f) => ({
                    ...f,
                    refundableOnly: e.target.checked || undefined,
                  }))
                }
              >
                Refundable only
              </Checkbox>
            ) : null}
            <Button
              onClick={() => {
                setDraftFilters({});
                setAppliedFilters({});
                setFiltersOpen(false);
              }}
            >
              {tripLodgingEn.resetFilters}
            </Button>
          </div>
        ) : (
          <Spin />
        )}
      </Modal>

      <SaveOfferModal
        open={Boolean(saveOffer)}
        offer={saveOffer}
        defaultCheckIn={defaultCheckIn}
        defaultCheckOut={defaultCheckOut}
        currency={displayCurrency}
        onClose={() => setSaveOffer(null)}
        onSave={(stay) => {
          onSaved(stay);
          setSaveOffer(null);
        }}
      />

      <BookOfferModal
        open={Boolean(bookOffer)}
        offer={bookOffer}
        defaultCheckIn={defaultCheckIn}
        defaultCheckOut={defaultCheckOut}
        currency={displayCurrency}
        countryCode={countryCode}
        onClose={() => setBookOffer(null)}
        onBooked={(stay) => {
          onSaved(stay);
          setBookOffer(null);
        }}
      />
    </div>
  );
};

const SaveOfferModal: React.FC<{
  open: boolean;
  offer: HotelSearchOffer | null;
  defaultCheckIn: string;
  defaultCheckOut: string;
  currency: string;
  onClose: () => void;
  onSave: (stay: TripHotelStay) => void;
}> = ({ open, offer, defaultCheckIn, defaultCheckOut, currency, onClose, onSave }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        checkInDate: dayjs(defaultCheckIn),
        checkOutDate: dayjs(defaultCheckOut),
        confirmationNumber: '',
      });
    }
  }, [open, defaultCheckIn, defaultCheckOut, form]);

  if (!offer) return null;
  const price = offerDisplayPrice(offer);

  return (
    <Modal
      open={open}
      title={tripLodgingEn.saveOfferTitle}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={tripLodgingEn.save}
    >
      <p>
        <strong>{offer.name}</strong>
      </p>
      {offer.roomName ? <p>{offer.roomName}</p> : null}
      {offer.formattedAddress ? <p>{offer.formattedAddress}</p> : null}
      {price != null ? (
        <p>
          {offer.currency || currency} {Math.round(price)}
        </p>
      ) : null}
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => {
          onSave(
            offerToStay(
              offer,
              v.checkInDate.format('YYYY-MM-DD'),
              v.checkOutDate.format('YYYY-MM-DD'),
              v.confirmationNumber
            )
          );
        }}
      >
        <Form.Item name="checkInDate" label={tripLodgingEn.checkIn} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="checkOutDate" label={tripLodgingEn.checkOut} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="confirmationNumber" label={tripLodgingEn.confirmationNumber}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const BookOfferModal: React.FC<{
  open: boolean;
  offer: HotelSearchOffer | null;
  defaultCheckIn: string;
  defaultCheckOut: string;
  currency: string;
  countryCode: string;
  onClose: () => void;
  onBooked: (stay: TripHotelStay) => void;
}> = ({
  open,
  offer,
  defaultCheckIn,
  defaultCheckOut,
  currency,
  countryCode,
  onClose,
  onBooked,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [reservationId, setReservationId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        checkInDate: dayjs(defaultCheckIn),
        checkOutDate: dayjs(defaultCheckOut),
        name: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: null,
        gender: 'OTHER',
        countryCode,
        isBusinessTrip: false,
        address: '',
      });
      setStatusMsg(null);
      setPaymentUrl(null);
      setReservationId(null);
      setPolling(false);
    }
  }, [open, defaultCheckIn, defaultCheckOut, countryCode, form]);

  const pollUntilDone = async (id: string, stayBase: TripHotelStay): Promise<void> => {
    setPolling(true);
    setStatusMsg(tripLodgingEn.bookingPending);
    const deadline = Date.now() + 60_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const res = await getHotelReservation(id);
        const status = (res.status || '').toUpperCase();
        if (status === 'CONFIRMED') {
          const conf =
            res.confirmationNumber?.trim() || id;
          onBooked({
            ...stayBase,
            reservationId: id,
            bookingStatus: status,
            bookingKey: null,
            confirmations: [{ id: crypto.randomUUID(), confirmationNumber: conf }],
          });
          message.success(tripLodgingEn.bookingConfirmed);
          setPolling(false);
          return;
        }
        if (status === 'FAILED' || status === 'CANCELLED') {
          setStatusMsg(tripLodgingEn.bookingFailed);
          setPolling(false);
          return;
        }
        if (res.paymentUrl) setPaymentUrl(res.paymentUrl);
      } catch {
        /* keep polling */
      }
    }
    setPolling(false);
    setStatusMsg(tripLodgingEn.bookingPending);
  };

  if (!offer) return null;
  const price = offerDisplayPrice(offer);

  return (
    <Modal
      open={open}
      title={tripLodgingEn.bookTitle}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
    >
      <p>
        <strong>{offer.name}</strong>
      </p>
      {offer.roomName ? <p>{offer.roomName}</p> : null}
      {price != null ? (
        <p>
          {offer.currency || currency} {Math.round(price)}
        </p>
      ) : null}
      {statusMsg ? <Alert type="info" message={statusMsg} showIcon style={{ marginBottom: 12 }} /> : null}
      {paymentUrl ? (
        <Button
          type="primary"
          href={paymentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginBottom: 12 }}
        >
          {polling ? tripLodgingEn.openPayment : tripLodgingEn.retryPayment}
        </Button>
      ) : null}
      <Form
        form={form}
        layout="vertical"
        onFinish={async (v) => {
          setSubmitting(true);
          setStatusMsg(null);
          try {
            const checkIn = v.checkInDate.format('YYYY-MM-DD');
            const checkOut = v.checkOutDate.format('YYYY-MM-DD');
            const redirectUrlAfterPayment = `${window.location.origin}${window.location.pathname}?hotelPaid=1`;
            const res = await createHotelReservation({
              offerId: offer.offerId,
              name: v.name.trim(),
              lastName: v.lastName.trim(),
              email: v.email.trim(),
              dateOfBirth: v.dateOfBirth.format('YYYY-MM-DD'),
              countryCode: String(v.countryCode || countryCode).toUpperCase().slice(0, 2),
              gender: v.gender,
              phone: v.phone.trim(),
              isBusinessTrip: Boolean(v.isBusinessTrip),
              redirectUrlAfterPayment,
              address: v.address?.trim() || undefined,
            });
            const id = res.reservationId;
            if (!id) throw new Error('Missing reservationId');
            setReservationId(id);
            if (res.paymentUrl) {
              setPaymentUrl(res.paymentUrl);
              window.open(res.paymentUrl, '_blank', 'noopener,noreferrer');
            }
            const stayBase = offerToStay(offer, checkIn, checkOut);
            stayBase.reservationId = id;
            stayBase.bookingStatus = res.status ?? 'PENDING_PAYMENT';
            await pollUntilDone(id, stayBase);
          } catch (err) {
            message.error(err instanceof Error ? err.message : tripLodgingEn.bookingFailed);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <Form.Item name="checkInDate" label={tripLodgingEn.checkIn} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="checkOutDate" label={tripLodgingEn.checkOut} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="name" label={tripLodgingEn.guestFirstName} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label={tripLodgingEn.guestLastName} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label={tripLodgingEn.guestEmail}
          rules={[{ required: true, type: 'email' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={tripLodgingEn.guestPhone} rules={[{ required: true }]}>
          <Input placeholder="+15551234567" />
        </Form.Item>
        <Form.Item name="dateOfBirth" label={tripLodgingEn.guestDob} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="gender" label={tripLodgingEn.guestGender} rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'MALE', label: tripLodgingEn.genderMale },
              { value: 'FEMALE', label: tripLodgingEn.genderFemale },
              { value: 'OTHER', label: tripLodgingEn.genderOther },
            ]}
          />
        </Form.Item>
        <Form.Item name="countryCode" label={tripLodgingEn.guestCountry} rules={[{ required: true }]}>
          <Input maxLength={2} />
        </Form.Item>
        <Form.Item name="address" label={tripLodgingEn.address}>
          <Input />
        </Form.Item>
        <Form.Item name="isBusinessTrip" label={tripLodgingEn.businessTrip} valuePropName="checked">
          <Switch />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting || polling} block>
          {tripLodgingEn.submitBooking}
        </Button>
        {reservationId ? (
          <Button
            style={{ marginTop: 8 }}
            block
            onClick={() => {
              const stayBase = offerToStay(
                offer,
                form.getFieldValue('checkInDate').format('YYYY-MM-DD'),
                form.getFieldValue('checkOutDate').format('YYYY-MM-DD')
              );
              stayBase.reservationId = reservationId;
              void pollUntilDone(reservationId, stayBase);
            }}
          >
            {tripLodgingEn.retry}
          </Button>
        ) : null}
      </Form>
    </Modal>
  );
};

export default HotelLiveSearchPanel;
